import React from "react";
import { SealCheck } from "@phosphor-icons/react";
import { useLocale } from "lib/hooks/useLocale";
import { CouponPageConfigType, PartnerOfferType, PartnerOffersDisplay } from "lib/types/merchantConfigTypes";
import { currencyFormatter } from "lib/utils/formatters";
import { extractRupeeValue } from "lib/utils/coupons";

interface PartnerOffersStickyFooterProps {
  partnerOffers: PartnerOfferType[];
  partnerOffersDisplay?: PartnerOffersDisplay | null;
  activeFilter: string;
  onCollectAll: () => void;
  primaryColor?: string;
  className?: string;
  customisations?: CouponPageConfigType;
}

const PartnerOffersStickyFooter: React.FC<PartnerOffersStickyFooterProps> = ({
  partnerOffers,
  partnerOffersDisplay,
  activeFilter,
  onCollectAll,
  primaryColor,
  className,
  customisations,
}) => {
  const { t } = useLocale();
  const visible = activeFilter === "PARTNER_OFFERS";
  if (!visible || !partnerOffers?.length) return null;

  const totalValue = partnerOffers.reduce(
    (sum, o) => sum + (o.offer_value ?? extractRupeeValue(o.title || o.description)),
    0,
  );
  const isCollected = (state: string) => state === "COLLECTED" || state === "ALREADY_APPLIED";

  const claimedValue = partnerOffers
    .filter((o) => isCollected(o.offer_state))
    .reduce((sum, o) => sum + (o.offer_value ?? extractRupeeValue(o.title || o.description)), 0);

  const allCollected = partnerOffers.every((o) => isCollected(o.offer_state));
  const noneCollected = partnerOffers.every((o) => !isCollected(o.offer_state));
  const useValueProgress = totalValue > 0 && partnerOffers.every((o) => o.offer_value != null);
  const progressPct = useValueProgress
    ? Math.min(100, (claimedValue / totalValue) * 100)
    : Math.min(
        100,
        (partnerOffers.filter((o) => isCollected(o.offer_state)).length / partnerOffers.length) * 100,
      );

  const totalText = useValueProgress ? currencyFormatter(totalValue) : null;
  const color = primaryColor ?? "#5C0F8B";

  const displayState = allCollected
    ? partnerOffersDisplay?.all_collected
    : noneCollected
      ? partnerOffersDisplay?.none_collected
      : partnerOffersDisplay?.partial_collected;

  const ctaText = t("partner_offers.collect_all");
  const allCollectedTitle = t("partner_offers.all_vouchers_claimed");

  return (
    <div
      className={`flex flex-col items-start gap-2 self-stretch border-t border-black/[0.08] bg-white/60 px-6 py-4${className ? ` ${className}` : ""}`}>
      {/* Row: claimed text / all-claimed state + button */}
      {allCollected ? (
        <div className="flex w-full items-center justify-between">
          <div className="flex items-center gap-2">
            <SealCheck size={24} weight="fill" className="shrink-0 text-yay-dark" />
            <span className="text-text-md-semibold text-coal-dark">{allCollectedTitle}</span>
          </div>
          <span className="text-text-xs-medium text-coal-dark opacity-50">
            {useValueProgress ? `${totalText} ${t("partner_offers.claimed")}` : `${partnerOffers.length}/${partnerOffers.length} ${t("partner_offers.claimed")}`}
          </span>
        </div>
      ) : (
        <div className="flex w-full items-center justify-between">
          {useValueProgress ? (
            <p className="text-text-sm-semibold text-coal-dark">
              {currencyFormatter(claimedValue)}{" "}
              <span className="text-text-xs-medium opacity-50">
                / {totalText} {t("partner_offers.claimed")}
              </span>
            </p>
          ) : (
            <p className="text-text-sm-semibold text-coal-dark">
              {partnerOffers.filter((o) => isCollected(o.offer_state)).length}{" "}
              <span className="text-text-xs-medium opacity-50">
                / {partnerOffers.length} {t("partner_offers.claimed")}
              </span>
            </p>
          )}
          <button
            type="button"
            className="flex h-8 shrink-0 items-center justify-center gap-2 border border-black/10 px-3"
            style={{ backgroundColor: color, borderRadius: customisations?.buttonRadius ?? 8 }}
            onClick={onCollectAll}>
            <span className="text-text-sm-semibold text-white">{ctaText}</span>
          </button>
        </div>
      )}
      {/* Progress bar */}
      <div className="h-1 w-full rounded-full bg-black/10">
        <div
          className="h-full rounded-full transition-all duration-300"
          style={{ width: `${progressPct}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

export default PartnerOffersStickyFooter;
